import type {ReactElement} from 'react';
import {useCallback, useEffect, useMemo, useRef} from 'react';
import {v4 as uuidv4} from 'uuid';
import {AiPromptBox} from '@Components/ai-prompt-box/ai-prompt-box';
import {PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import type {FooterLeftAction, FooterLeftActionProps} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-left-actions/ai-prompt-box-footer-left-actions.types';
import {AiPromptBoxUploads} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-uploads/ai-prompt-box-uploads';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {addItemsToGrid, deleteGridItems} from '@Components/base-grid';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import {AiVideoGenerationStatus} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import {
  addAiVideoGenerationItem,
  markAiVideoGenerationFailed,
  removeAiVideoGenerationItem,
  selectAiCredits,
  selectGeneratingAiVideoCount,
  selectPendingAiVideoRetry,
  setAiCredits,
} from '@Components/poster-editor/poster-editor-reducer';
import type {BuyCreditsResponse} from '@/types/credits.types';
import {hideLoading} from '@Libraries/loading-toast-library';
import {noop} from '@Utils/general.util';
import {isUserPremium} from '@Libraries/user.library';
import type {AiVideoBackendData, AiVideoReferenceImage, GenerateAiVideoParams} from '@Libraries/ai-video.library';
import {AiVideoGenerationError, AiVideoGenerationErrorCode, AiVideoReferenceImageType, generateAiVideo, showAiVideoUpsellDialog} from '@Libraries/ai-video.library';
import {
  AI_VIDEO_GENERATION_LOADING_KEY,
  DEFAULT_AI_VIDEO_ASPECT_RATIO,
  DEFAULT_AI_VIDEO_AUDIO,
  DEFAULT_AI_VIDEO_CAMERA_ANGLE,
  DEFAULT_AI_VIDEO_QUALITY,
  getAiVideoCreditCost,
  getAiVideoSkeletonDimensions,
  getPromptBoxOptions,
  MAX_CONCURRENT_AI_VIDEO_GENERATIONS,
} from '@Panels/ai-video-panel/ai-video-panel.library';
import {AI_VIDEO_PROMPT_BOX_ID, AiVideoAspectRatio, AiVideoAudio, AiVideoCameraAngle, AiVideoFilterKey, AiVideoQuality, AiVideoTextSetting} from '@Panels/ai-video-panel/ai-video-panel.types';
import {AiVideoSettingsPills} from '@Panels/ai-video-panel/components/ai-video-settings-pills/ai-video-settings-pills';
import {AI_VIDEO_LOADING_SKELETON_ID_PREFIX, GENERATED_VIDEO_GRID_ID} from '@Panels/ai-video-panel/components/generated-video-grid/generated-video-grid.types';
import {buildSkeletonGridItem} from '@Components/lazy-masonry-grid/generated-media-grid-library';
import {GeneratedVideoItem} from '@Panels/ai-video-panel/components/generated-video-item/generated-video-item';
import type {UserImageAttachment} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {PromptAttachmentType} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {isUserImageAttachment} from '@Components/ai-prompt-box/libraries/ai-prompt-box-pending-uploads.library';
import {isMobile, isTablet} from 'react-device-detect';
import {stringToEnum, stringToEnumOrNull} from '@Utils/string.util';
import {isPremiumPlusUser} from '@Utils/user.util';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {updateAiCredits} from '@Libraries/pmw-stock-media-library';

interface AiVideoPromptBoxProps {
  isFullScreen?: boolean;
  onFullscreenPreview: (video: AiVideoBackendData) => void;
  onVideoGenerated: (videos: AiVideoBackendData[]) => void;
}

export function AiVideoPromptBox({isFullScreen = false, onFullscreenPreview, onVideoGenerated}: AiVideoPromptBoxProps): ReactElement {
  const dispatch = useAppDispatch();
  const onFullscreenPreviewRef = useRef(onFullscreenPreview);
  onFullscreenPreviewRef.current = onFullscreenPreview;
  const pendingRetry = useAppSelector(selectPendingAiVideoRetry);
  const totalCredits = useAppSelector(selectAiCredits);
  const {actions, state, selectedContextualMenuItems} = useAiPromptBox(AI_VIDEO_PROMPT_BOX_ID);
  const generatingCount = useAppSelector(selectGeneratingAiVideoCount);
  const promptBoxStateResetTimeoutRef = useRef<number | null>(null);
  const hasLimitReached = generatingCount >= MAX_CONCURRENT_AI_VIDEO_GENERATIONS;

  const qualityKey: string = AiVideoFilterKey.QUALITY;
  const audioKey: string = AiVideoFilterKey.AUDIO;
  const aspectRatioKey: string = AiVideoFilterKey.ASPECT_RATIO;
  const textSettingKey: string = AiVideoFilterKey.TEXT_SETTING;

  const getSelectedQuality = (): AiVideoQuality => {
    return stringToEnum(selectedContextualMenuItems[qualityKey]?.[0], Object.values(AiVideoQuality), DEFAULT_AI_VIDEO_QUALITY);
  };

  const getSelectedAudio = (): AiVideoAudio => {
    return stringToEnum(selectedContextualMenuItems[audioKey]?.[0], Object.values(AiVideoAudio), DEFAULT_AI_VIDEO_AUDIO);
  };

  const getSelectedAspectRatio = (): AiVideoAspectRatio => {
    return stringToEnum(selectedContextualMenuItems[aspectRatioKey]?.[0], Object.values(AiVideoAspectRatio), DEFAULT_AI_VIDEO_ASPECT_RATIO);
  };

  const getSelectedTextSetting = (): AiVideoTextSetting | null => {
    return stringToEnumOrNull(selectedContextualMenuItems[textSettingKey]?.[0], Object.values(AiVideoTextSetting));
  };

  const getCurrentCreditCost = (): number => {
    return getAiVideoCreditCost(getSelectedQuality(), getSelectedAudio());
  };

  useEffect(() => {
    if (!totalCredits) {
      updateAiCredits();
    }
    actions.update({promptBoxState: PromptBoxState.ACTIVE});
    return (): void => {
      if (promptBoxStateResetTimeoutRef.current !== null) {
        window.clearTimeout(promptBoxStateResetTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (totalCredits === null || totalCredits === state.creditsPill?.remainingCredits) {
      return;
    }
    actions.update({creditsPill: {remainingCredits: totalCredits}});
  }, [totalCredits]);

  useEffect(() => {
    const pillVisibility = [PromptBoxState.ACTIVE, PromptBoxState.LOADING].includes(state.promptBoxState);
    if (pillVisibility !== state.creditsPill?.visible) {
      actions.update({
        creditsPill: {
          visible: pillVisibility,
        },
      });
    }
  }, [state.promptBoxState]);

  useEffect(() => {
    const newCost = getCurrentCreditCost();
    if (newCost !== state.creditsPill?.actionCost) {
      actions.update({
        creditsPill: {
          actionCost: newCost,
        },
      });
    }
  }, [selectedContextualMenuItems[qualityKey], selectedContextualMenuItems[audioKey]]);

  useEffect(() => {
    const message = hasLimitReached ? window.i18next.t('pmwjs_ai_video_limit_reached_try_again_soon') : '';
    actions.update({
      isDailyLimitReached: hasLimitReached,
      showDailyLimitError: false,
      dailyLimitErrorMessage: message.length > 0 ? message : undefined,
    });
  }, [hasLimitReached]);

  const cameraAngleKey: string = AiVideoFilterKey.CAMERA_ANGLE;

  const getSelectedCameraAngle = (): AiVideoCameraAngle => {
    return stringToEnum(selectedContextualMenuItems[cameraAngleKey]?.[0], Object.values(AiVideoCameraAngle), DEFAULT_AI_VIDEO_CAMERA_ANGLE);
  };

  const getReferenceImageType = (attachment: UserImageAttachment): AiVideoReferenceImageType => {
    return attachment.type === PromptAttachmentType.REFERENCE_IMAGE ? AiVideoReferenceImageType.AI_ATTACHMENT : AiVideoReferenceImageType.USER_IMAGE;
  };

  const getReferenceImages = (): AiVideoReferenceImage[] => {
    return (state.attachments?.items ?? [])
      .filter(isUserImageAttachment)
      .filter((attachment) => attachment.hashedFilename !== '')
      .map((attachment) => ({hashedFilename: attachment.hashedFilename, type: getReferenceImageType(attachment)}));
  };

  const promptBoxUploadsOption: FooterLeftAction = useCallback(
    (footerProps: FooterLeftActionProps): ReactElement | null => (
      <AiPromptBoxUploads
        {...footerProps}
        renderAttachmentTooltip={!isMobile && !isTablet}
        defaultTooltipText={window.i18next.t('pmwjs_add_reference_images')}
      />
    ),
    []
  );

  const promptBoxOptions = useMemo(() => [promptBoxUploadsOption], [promptBoxUploadsOption]);

  const handleInsufficientCreditsError = (): void => {
    window.PMW.util.require(typeof window.PMW.stripeCheckout === 'undefined', 'stripeCheckoutDialog', true, () => {
      window.PMW.stripeCheckout.showStripeCheckoutForCreditPackPurchase(
        (data: BuyCreditsResponse) => {
          if (data.credits > 0) {
            dispatch(setAiCredits(data.credits - (state.creditsPill?.actionCost ?? getCurrentCreditCost())));
            void onGenerate(data.credits);
          }
        },
        noop,
        isPremiumPlusUser() ? window.i18next.t('pmwjs_purchase_more_credits_for_ai') : window.i18next.t('pmwjs_purchase_more_credits_for_ai_with_upgrade_message'),
        window.i18next.t('pmwjs_youre_out_of_credits')
      );
    });
  };

  const hasEnoughCredits = (creditCost: number, freshRemainingCredits?: number): boolean => {
    const remaining = freshRemainingCredits ?? state.creditsPill?.remainingCredits ?? 0;
    return remaining >= creditCost;
  };

  const getCreditCostForParams = (generationParams: GenerateAiVideoParams): number => {
    const quality = stringToEnum(generationParams.quality, Object.values(AiVideoQuality), DEFAULT_AI_VIDEO_QUALITY);
    return getAiVideoCreditCost(quality, generationParams.generateAudio ? AiVideoAudio.YES : AiVideoAudio.NO);
  };

  const schedulePromptBoxStateReset = (): void => {
    if (promptBoxStateResetTimeoutRef.current !== null) {
      window.clearTimeout(promptBoxStateResetTimeoutRef.current);
    }
    promptBoxStateResetTimeoutRef.current = window.setTimeout(() => {
      promptBoxStateResetTimeoutRef.current = null;
      actions.update({promptBoxState: PromptBoxState.ACTIVE});
    }, 1000);
  };

  const onGenerate = async (freshRemainingCredits?: number): Promise<void> => {
    if (!isUserPremium()) {
      await showAiVideoUpsellDialog();
      return;
    }

    const prompt = state.prompt.trim();
    if (!prompt) {
      return;
    }

    if (hasLimitReached) {
      return;
    }

    if (!hasEnoughCredits(getCurrentCreditCost(), freshRemainingCredits)) {
      handleInsufficientCreditsError();
      return;
    }

    actions.update({error: null, promptBoxState: PromptBoxState.LOADING});
    schedulePromptBoxStateReset();

    const referenceImages = getReferenceImages();
    const params: GenerateAiVideoParams = {
      prompt,
      aspectRatio: getSelectedAspectRatio(),
      quality: getSelectedQuality(),
      generateAudio: getSelectedAudio() === AiVideoAudio.YES,
      cameraAngle: getSelectedCameraAngle(),
      textSetting: getSelectedTextSetting() ?? undefined,
      referenceImages: referenceImages.length > 0 ? referenceImages : undefined,
    };
    await runGeneration(params);
  };

  const buildGeneratedVideoGridItem = (response: AiVideoBackendData): GridItemStorage => ({
    type: GRID_ITEM_TYPE.CUSTOM,
    id: `${response.id}`,
    width: response.videoProps.width,
    height: response.videoProps.height,
    data: {video: response},
    children: (
      <GeneratedVideoItem
        key={response.id}
        item={response}
        onFullscreenPreview={(): void => {
          onFullscreenPreviewRef.current(response);
        }}
        autoPlayOnMount
      />
    ),
  });

  const removeLoadingSkeleton = (id: string): void => {
    dispatch(deleteGridItems({gridId: GENERATED_VIDEO_GRID_ID, gridSelectionGroupId: GENERATED_VIDEO_GRID_ID, itemIdsToDelete: [id]}));
  };

  const handleGenerationSuccess = (id: string, response: AiVideoBackendData): void => {
    dispatch(removeAiVideoGenerationItem(id));
    removeLoadingSkeleton(id);
    const remainingLoadingCount = selectGeneratingAiVideoCount(window.PMW.redux.store.getState());
    dispatch(addItemsToGrid({id: GENERATED_VIDEO_GRID_ID, items: [buildGeneratedVideoGridItem(response)], index: remainingLoadingCount}));
    onVideoGenerated([response]);
  };

  const handleGenerationError = (id: string, e: unknown): void => {
    removeLoadingSkeleton(id);
    const errorCode = e instanceof AiVideoGenerationError ? e.code : undefined;
    if (errorCode === AiVideoGenerationErrorCode.INSUFFICIENT_CREDITS) {
      dispatch(removeAiVideoGenerationItem(id));
      handleInsufficientCreditsError();
      return;
    }
    dispatch(markAiVideoGenerationFailed({id, errorCode}));
    console.error(e);
  };

  const hideLoadingToastIfDone = (): void => {
    if (selectGeneratingAiVideoCount(window.PMW.redux.store.getState()) === 0) {
      hideLoading(AI_VIDEO_GENERATION_LOADING_KEY);
    }
  };

  const runGeneration = async (params: GenerateAiVideoParams): Promise<void> => {
    const id = `${AI_VIDEO_LOADING_SKELETON_ID_PREFIX}${uuidv4()}`;
    const {width, height} = getAiVideoSkeletonDimensions(params.aspectRatio as AiVideoAspectRatio);

    dispatch(addAiVideoGenerationItem({id, status: AiVideoGenerationStatus.GENERATING, params}));
    dispatch(addItemsToGrid({id: GENERATED_VIDEO_GRID_ID, items: [buildSkeletonGridItem({id, width, height})], index: 0}));
    const remainingCredits = totalCredits - getCreditCostForParams(params);
    if (remainingCredits >= 0) {
      dispatch(setAiCredits(remainingCredits));
    }

    try {
      const response = await generateAiVideo(params);
      handleGenerationSuccess(id, response);
    } catch (e) {
      handleGenerationError(id, e);
    }
    updateAiCredits();
    hideLoadingToastIfDone();
  };

  useEffect(() => {
    if (!pendingRetry) {
      return;
    }
    dispatch(removeAiVideoGenerationItem(pendingRetry.id));
    void runGeneration(pendingRetry.params);
  }, [pendingRetry, dispatch]);

  return (
    <>
      <AiPromptBox
        id={AI_VIDEO_PROMPT_BOX_ID}
        containerClasses={hasLimitReached && isFullScreen && !isMobile ? 'spacing-p-t-6' : ''}
        initialConfig={getPromptBoxOptions(totalCredits ?? 0)}
        onGenerate={onGenerate}
        onRegenerate={onGenerate}
        options={promptBoxOptions}
        focus={true}
        allowSoftSendDuringUploads={false}
      />
      <AiVideoSettingsPills className={`spacing-m-t-3 ${hasLimitReached && !(isFullScreen && !isMobile) ? 'spacing-m-b-3' : 'spacing-m-b-6'}`} />
    </>
  );
}
